import React, { useRef, useState, useEffect } from "react";
import { IoSendSharp } from "react-icons/io5";

/* =========================================================
   ZOOM ANIMATION STYLES
========================================================= */
const chatWindowStyle = {
  animation: "chatZoomIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
};

const keyframesStyle = `
  @keyframes chatZoomIn {
    0% {
      opacity: 0;
      transform: scale(0.82) translateY(16px);
      transform-origin: bottom right;
    }
    100% {
      opacity: 1;
      transform: scale(1) translateY(0);
      transform-origin: bottom right;
    }
  }
`;

/* =========================================================
   KNOWLEDGE BASE
========================================================= */
const knowledgeBase = [
  {
    keywords: [
      "what is axtelica",
      "about the company",
      "who are you",
      "company overview",
      "about axtelica",
    ],
    responses: [
      `Axtelica is a next-generation AI, analytics, and digital transformation company helping organizations transform raw data into actionable business intelligence using cloud, automation, and advanced AI solutions.`,
    ],
  },

  {
    keywords: [
      "why choose",
      "different",
      "stand out",
      "why axtelica",
      "why your company",
    ],
    responses: [
      `We stand out through:

• Problem-first thinking
• Practical AI with measurable ROI
• Enterprise-grade scalable architecture
• Faster implementation cycles
• Cost-smart modular delivery
• Reliable cloud and data engineering expertise`,
    ],
  },

  {
    keywords: ["vision", "mission", "goal"],
    responses: [
      `Our mission is to empower organizations with scalable AI and intelligent data systems that simplify complexity and accelerate business growth.`,
    ],
  },

  {
    keywords: [
      "services",
      "offerings",
      "what do you offer",
      "solutions",
      "capabilities",
    ],
    responses: [
      `Our services include:

• AI & Data Engineering
• Business Intelligence & Analytics
• Cloud & DevOps Solutions
• Real-Time Data Integration
• Digital Transformation
• Workflow Automation
• Talent-as-a-Service`,
    ],
  },

  {
    keywords: [
      "infinisight",
      "analytics platform",
      "bi platform",
      "dashboard platform",
      "business intelligence",
    ],
    responses: [
      `Infinisight is Axtelica's AI-powered analytics platform built for predictive intelligence, KPI monitoring, smart dashboards, and natural language business querying.`,
    ],
  },

  {
    keywords: [
      "infinisight features",
      "smart query",
      "dashboard",
      "prediction",
      "forecasting",
      "analytics features",
    ],
    responses: [
      `Key Infinisight capabilities include:

• Real-time KPI dashboards
• Predictive analytics
• Natural language querying
• Unified data integration
• Trend forecasting
• Workflow intelligence`,
    ],
  },

  {
    keywords: [
      "axtream",
      "real-time integration",
      "pipeline automation",
      "streaming platform",
      "api orchestration",
      "real time ingest",
      "streaming",
    ],
    responses: [
      `Axtream is our intelligent real-time integration platform designed for scalable API orchestration, streaming pipelines, and AI-ready operational workflows.`,
    ],
  },

  {
    keywords: [
      "axtream benefits",
      "real time visibility",
      "streaming benefits",
      "pipeline benefits",
    ],
    responses: [
      `Axtream helps organizations achieve:

• Real-time operational visibility
• Faster data movement
• Improved pipeline reliability
• Better data quality governance
• Faster business decisions`,
    ],
  },

  {
    keywords: [
      "ai agents",
      "business agents",
      "digital colleague",
      "automation agents",
      "intelligent agents",
    ],
    responses: [
      `Axtelica develops AI Business Agents that function like intelligent digital colleagues to automate workflows, validation, documentation, and operational tasks.`,
    ],
  },

  {
    keywords: [
      "requirement agent",
      "capture requirements",
      "documentation agent",
    ],
    responses: [
      `The Requirement Agent captures business needs through conversational workflows and converts them into structured actionable documentation.`,
    ],
  },

  {
    keywords: [
      "design agent",
      "architecture agent",
      "solution design",
    ],
    responses: [
      `The Design Agent transforms requirements into scalable architecture recommendations, workflows, and implementation-ready solution designs.`,
    ],
  },

  {
    keywords: [
      "validation agent",
      "quality check agent",
      "validation",
    ],
    responses: [
      `The Validation Agent automatically detects inconsistencies, logic mismatches, and data quality issues in real time.`,
    ],
  },

  {
    keywords: [
      "workflow agent",
      "automation agent",
      "workflow automation",
    ],
    responses: [
      `The Workflow Agent automates repetitive operational processes, task routing, approvals, and execution workflows with minimal manual intervention.`,
    ],
  },

  {
    keywords: [
      "cloud",
      "devops",
      "aws",
      "azure",
      "databricks",
      "tech stack",
      "technology",
    ],
    responses: [
      `Our technology ecosystem includes AWS, Azure, Databricks, PySpark, Power BI, Tableau, GitLab DevOps, and scalable cloud-native architectures.`,
    ],
  },

  {
    keywords: [
      "security",
      "compliance",
      "data protection",
      "governance",
      "encryption",
    ],
    responses: [
      `Axtelica follows enterprise-grade security practices including governance frameworks, encrypted data handling, access controls, and compliance-ready architectures.`,
    ],
  },

  {
    keywords: [
      "pricing",
      "demo",
      "consultation",
      "contact",
      "book demo",
      "get in touch",
    ],
    responses: [
      `We'd be happy to help with pricing, consultations, and solution discussions.

You can connect with our team for:

• Product demonstrations
• AI transformation consulting
• Architecture discussions
• Custom implementation planning`,
    ],
  },

  {
    keywords: ["results", "impact", "benefits", "outcomes"],
    responses: [
      `Organizations working with Axtelica typically achieve improved operational efficiency, faster decision-making, scalable automation, and better business visibility through AI-driven systems.`,
    ],
  },
  {
  keywords: [
    "what is delivery ai",
    "delivery ai",
    "delivery platform",
    "delivery ai overview",
    "about delivery ai",
  ],
  responses: [
    `Delivery AI helps teams transform business ideas into clear requirements, practical designs, and validation-ready outputs. It enables business, technology, and QA teams to collaborate effectively before development begins, reducing gaps, improving quality, and accelerating project delivery.`,
  ],
},

{
  keywords: [
    "requirement studio agent",
    "requirements agent",
    "brd frd generation",
    "requirement gathering",
    "business requirements",
  ],
  responses: [
    `Requirement Studio Agent helps teams capture business needs and convert them into structured requirement documents. It generates BRD, FRD, acceptance criteria, identifies assumptions, risks, dependencies, and creates business-friendly summaries to improve clarity and reduce rework before development starts.`,
  ],
},

{
  keywords: [
    "designer agent",
    "solution design",
    "workflow design",
    "process flows",
    "system design",
  ],
  responses: [
    `Designer Agent converts approved requirements into practical design outputs. It creates solution design summaries, workflows, process flows, recommends screens, modules, APIs, integrations, and maps how data and systems interact to ensure smooth development planning and execution.`,
  ],
},

{
  keywords: [
    "validatai agent",
    "validation agent",
    "requirement validation",
    "test scenarios",
    "quality assurance",
  ],
  responses: [
    `ValidatAI Agent helps teams validate requirements, designs, and business rules before execution. It identifies missing rules, gaps, inconsistencies, generates validation rules and test scenarios, and highlights risks early to improve quality and reduce costly rework.`,
  ],
},

{
  keywords: [
    "delivery ai features",
    "key features of delivery ai",
    "what can delivery ai do",
    "delivery ai capabilities",
  ],
  responses: [
    `Delivery AI converts notes, documents, and business inputs into structured delivery outputs. It supports requirements, design flows, validation rules, and test scenarios while identifying missing details, unclear logic, and process gaps early in the project lifecycle.`,
  ],
},

{
  keywords: [
    "delivery ai benefits",
    "advantages of delivery ai",
    "why use delivery ai",
    "delivery ai value",
  ],
  responses: [
    `Delivery AI accelerates project discovery and planning, reduces manual documentation effort, minimizes gaps before development, improves collaboration across teams, and enhances delivery quality and governance.`,
  ],
},

{
  keywords: [
    "how does delivery ai work",
    "delivery ai process",
    "delivery ai workflow",
  ],
  responses: [
    `Delivery AI uses specialized AI agents to guide teams through requirement gathering, design preparation, and validation. It transforms business ideas into structured outputs, aligns stakeholders, and ensures requirements and designs are validated before development begins.`,
  ],
},

{
  keywords: [
    "delivery ai tagline",
    "delivery ai slogan",
    "short description of delivery ai",
  ],
  responses: [
    `Turn business ideas into clear requirements, practical designs, and validation-ready outputs with AI agents.`,
  ],
},
];

/* =========================================================
   GENERIC RESPONSES
========================================================= */
const genericResponses = [
  "That's a great question. Let me help you with that.",
  "I'd be happy to guide you regarding Axtelica's solutions.",
  "Our team specializes in scalable AI and data-driven systems.",
  "Axtelica focuses on practical AI implementation with measurable business outcomes.",
];

/* =========================================================
   SUGGESTION CHIPS
========================================================= */
const suggestionChips = [
  "Infinisight",
  "Axtream",
  "AI Agents",
  "Cloud & DevOps",
  "Pricing",
  "Book Demo",
];

/* =========================================================
   LEAD CAPTURE QUESTIONS
========================================================= */
const leadQuestions = [
  "May I know your name?",
  "Please share your business email.",
  "Which company are you representing?",
  "Briefly describe your requirement or project goal.",
];

/* =========================================================
   INTENT DETECTION
========================================================= */
function detectIntent(message) {
  const lowerMsg = message.toLowerCase();

  let bestMatch = null;
  let maxScore = 0;

  knowledgeBase.forEach((item) => {
    let score = 0;

    item.keywords.forEach((keyword) => {
      if (lowerMsg.includes(keyword.toLowerCase())) {
        score += keyword.length;
      }
    });

    if (score > maxScore) {
      maxScore = score;
      bestMatch = item;
    }
  });

  if (maxScore >= 1 && bestMatch) {
    const intro =
      genericResponses[Math.floor(Math.random() * genericResponses.length)];

    return `${intro}

${bestMatch.responses[0]}`;
  }

  return `Thank you for your query.

I can help you with information related to:

• Axtelica services and solutions
• AI & Data Engineering
• Infinisight Analytics Platform
• Axtream Real-Time Integration
• AI Business Agents
• DevOps & Cloud Solutions
• Digital Transformation Services
• Pricing, demos, and consultations

Please share a little more detail about what you're looking for, and I'll guide you further.`;
}

/* =========================================================
   COMPONENT
========================================================= */
export default function Chatbot() {
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: `Hello! I'm AXA — Axtelica's AI Assistant.

I'm here to help you explore our AI, analytics, cloud, and digital transformation solutions.

You can ask about:

• Services & capabilities
• Infinisight platform
• Axtream integration
• AI business agents
• Industry use cases
• Pricing & consultations`,
    },
  ]);

  const [input, setInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const [messageCount, setMessageCount] = useState(0);

  const [leadMode, setLeadMode] = useState(false);
  const [leadStep, setLeadStep] = useState(0);

  const chatEndRef = useRef();

  /* =========================================================
     AUTO SCROLL
  ========================================================= */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  /* =========================================================
     OPEN CHATBOT EVENT
  ========================================================= */
  useEffect(() => {
    const openChatbot = () => setIsOpen(true);
    window.addEventListener("open-chatbot", openChatbot);
    return () => window.removeEventListener("open-chatbot", openChatbot);
  }, []);

  /* =========================================================
     SEND MESSAGE
  ========================================================= */
  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage = input;

    setMessages((prev) => [...prev, { sender: "user", text: userMessage }]);
    setInput("");
    setIsTyping(true);

    /* ================= LEAD CAPTURE ================= */
    if (leadMode) {
      setTimeout(() => {
        if (leadStep < leadQuestions.length - 1) {
          setMessages((prev) => [
            ...prev,
            { sender: "bot", text: leadQuestions[leadStep + 1] },
          ]);
          setLeadStep((prev) => prev + 1);
        } else {
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              text: `Thank you for sharing your details.

Our team will review your requirements and connect with you shortly regarding the next steps.`,
            },
          ]);
          setLeadMode(false);
          setLeadStep(0);
        }
        setIsTyping(false);
      }, 1200);

      return;
    }

    /* ================= NORMAL CHAT ================= */
    setTimeout(() => {
      const botReply = detectIntent(userMessage);

      setMessages((prev) => [...prev, { sender: "bot", text: botReply }]);
      setIsTyping(false);

      const newCount = messageCount + 1;
      setMessageCount(newCount);

      /* ================= LEAD TRIGGER ================= */
      if (newCount === 4) {
        setTimeout(() => {
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              text: `Would you like our team to contact you for a consultation or product discussion?`,
            },
          ]);
        }, 1500);
      }
    }, 1200);
  };

  /* =========================================================
     START LEAD CAPTURE
  ========================================================= */
  const startLeadCapture = () => {
    setLeadMode(true);
    setLeadStep(0);
    setMessages((prev) => [
      ...prev,
      { sender: "bot", text: leadQuestions[0] },
    ]);
  };

  /* =========================================================
     CHIP CLICK
  ========================================================= */
  const handleChipClick = (chip) => {
    if (chip === "Book Demo") {
      startLeadCapture();
      return;
    }
    setInput(chip);
  };

  return (
    <>
      {/* Inject keyframe animation into the document */}
      <style>{keyframesStyle}</style>

      {/* =========================================================
          FLOATING BUTTON
      ========================================================= */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-4 sm:right-6 w-14 h-14 rounded-full bg-white shadow-2xl hover:scale-110 transition z-50 flex items-center justify-center border border-gray-200"
      >
        <img src="/assets/chat_icon.png" alt="chat" className="w-10 h-10" />
      </button>

      {/* =========================================================
          CHAT WINDOW — zoom-in on open
      ========================================================= */}
      {isOpen && (
        <div
          style={chatWindowStyle}
          className="fixed bottom-24 right-2 sm:right-4
            w-[95%] sm:w-[390px] md:w-[430px]
            h-[78vh] max-h-[680px]
            bg-white rounded-2xl shadow-2xl
            border border-gray-200
            flex flex-col overflow-hidden z-50"
        >
          {/* =========================================================
              HEADER
          ========================================================= */}
          <div className="bg-[linear-gradient(135deg,#11258f_0%,#1a35ad_40%,#2b4ec9_75%,#3d63e3_100%)] px-4 py-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <img
                src="/assets/axa_chat_icon.png"
                alt="logo"
                className="w-8 h-8"
              />
              <div>
                <h2 className="text-white font-semibold text-sm">
                  AXA AI Assistant
                </h2>
                <p className="text-blue-100 text-xs">Enterprise AI Solutions</p>
              </div>
            </div>

            <button
              onClick={() => setIsOpen(false)}
              className="text-white text-lg"
            >
              ✕
            </button>
          </div>

          {/* =========================================================
              MESSAGES
          ========================================================= */}
          <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${
                  msg.sender === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap shadow-sm ${
                    msg.sender === "user"
                      ? "bg-[#3d63e3] text-white"
                      : "bg-white border border-gray-200 text-gray-800"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {/* =========================================================
                TYPING INDICATOR
            ========================================================= */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 text-gray-500 px-4 py-3 rounded-2xl text-sm shadow-sm">
                  AXA is typing...
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* =========================================================
              SUGGESTION CHIPS — flex-wrap instead of horizontal scroll
          ========================================================= */}
          <div className="px-3 py-2 border-t bg-white flex flex-wrap gap-2">
            {suggestionChips.map((chip, i) => (
              <button
                key={i}
                onClick={() => handleChipClick(chip)}
                className="whitespace-nowrap px-3 py-1 text-xs rounded-full bg-blue-50 text-[#11258f] border border-blue-200 hover:bg-blue-100 transition"
              >
                {chip}
              </button>
            ))}
          </div>

          {/* =========================================================
              INPUT
          ========================================================= */}
          <div className="p-3 border-t bg-white text-black flex items-center gap-2">
            <input
              type="text"
              placeholder="Ask about AI, analytics, cloud, pricing..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              className="flex-1
                px-4 py-2.5
                rounded-xl border border-gray-300
                outline-none
                focus:ring-2 focus:ring-[#3d63e3]
                text-[13px] sm:text-sm
                min-w-0"
            />

            <button
              onClick={handleSend}
              className="w-11 h-11 rounded-xl bg-[#11258f] text-white flex items-center justify-center hover:scale-105 transition"
            >
              <IoSendSharp size={20} />
            </button>
          </div>

          {/* =========================================================
              FOOTER
          ========================================================= */}
          <div className="text-center text-xs text-gray-500 py-2 bg-gray-50">
            Powered by Techquitoes
          </div>
        </div>
      )}
    </>
  );
}